import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook } from "lucide-react";
import { BRAND, NAV, SERVICES, SOCIAL, LOGOS } from "../data/content";
import { openConsentBanner } from "../lib/cookie-consent";
import NewsletterForm from "./NewsletterForm";

const iconMap = { Linkedin, Instagram, Facebook };

const Footer = () => {
  return (
    <footer
      className="bg-dodgers text-galactico section-pad relative overflow-hidden"
      data-testid="site-footer"
    >
      <div className="container-os">
        {/* Newsletter signup inline */}
        <div
          className="border-b border-galactico/15 pb-10 md:pb-14 mb-12 md:mb-16"
          data-testid="footer-newsletter"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
            <div>
              <h3 className="font-display text-[28px] md:text-[36px] leading-tight text-galactico">
                Iscriviti alla <span className="text-lime">NEWSLETTER.</span>
              </h3>
              <p className="text-galactico/70 mt-3 text-[15px] leading-relaxed max-w-[420px]">
                La sponsorship sportiva italiana, decodificata. Ogni due settimane.
              </p>
            </div>
            <div>
              <NewsletterForm variant="inline" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
          {/* Brand + dati societari */}
          <div className="md:col-span-4">
            <img src={LOGOS.whiteWordmark} alt="OnSports" className="h-9 mb-6" />
            <p className="font-condensed uppercase tracking-widest text-sm opacity-80 mb-6">
              {BRAND.tagline}
            </p>
            <address className="not-italic text-[15px] leading-relaxed mb-6">
              <p>{BRAND.legalName}</p>
              <p>{BRAND.addressStreet}</p>
              <p>{BRAND.addressCity}</p>
            </address>
            <div className="text-[13px] leading-relaxed opacity-70 space-y-1">
              <p>{BRAND.vat}</p>
              <p>{BRAND.sdi}</p>
              <p>{BRAND.capital}</p>
            </div>
          </div>
          {/* Naviga */}
          <div className="md:col-span-2">
            <h4 className="font-condensed uppercase tracking-widest text-xs opacity-60 mb-5">
              Naviga
            </h4>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.href}>
                  <Link to={n.href} className="hover:text-lime transition-colors text-[15px]">
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          {/* Servizi */}
          <div className="md:col-span-3">
            <h4 className="font-condensed uppercase tracking-widest text-xs opacity-60 mb-5">
              Servizi
            </h4>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/servizi/${s.slug}`}
                    className="hover:text-lime transition-colors text-[15px]"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          {/* Contatti */}
          <div className="md:col-span-3">
            <h4 className="font-condensed uppercase tracking-widest text-xs opacity-60 mb-5">
              Contatti
            </h4>
            <ul className="space-y-3 text-[15px]">
              <li>
                <a className="hover:text-lime transition-colors" href={`mailto:${BRAND.email}`}>
                  {BRAND.email}
                </a>
              </li>
              <li>
                <a
                  className="hover:text-lime transition-colors"
                  href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
                >
                  {BRAND.phone}
                </a>
              </li>
            </ul>
            <div className="flex items-center gap-4 mt-6">
              {SOCIAL.map((s) => {
                const Icon = iconMap[s.icon];
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 inline-flex items-center justify-center border border-galactico/30 hover:border-lime hover:text-lime transition-colors"
                    aria-label={`OnSports su ${s.label}`}
                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                  >
                    {Icon && <Icon size={16} />}
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <hr className="hr-os mt-16 mb-8" />

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm opacity-70">
          <p>© 2026 {BRAND.legalName} · {BRAND.vat}</p>
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            <li>
              <Link className="hover:text-lime" to="/privacy-policy" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link className="hover:text-lime" to="/cookie-policy" data-testid="footer-cookie-link">
                Cookie Policy
              </Link>
            </li>
            <li>
              <button
                type="button"
                onClick={openConsentBanner}
                className="hover:text-lime underline-offset-4 hover:underline transition-colors"
                data-testid="footer-manage-cookies-btn"
              >
                Gestisci cookie
              </button>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
